import {
  ApplicantGroupType,
  ExamineeImportMethod,
  ExamType,
  RegistrationType,
} from '@exam-center/graphql';
import { dayjs } from '@shared/utils';
import { type Path } from 'react-hook-form';
import { z, ZodIssueCode } from 'zod';

export const BaseExamEventCreationFormSchema = z.object({
  examEventName: z.string().trim().optional(),
  examType: z.nativeEnum(ExamType).optional(),
  registrationType: z.nativeEnum(RegistrationType).optional(),
  examineeImportMethod: z.nativeEnum(ExamineeImportMethod).optional(),
  examCourses: z
    .array(
      z.object({
        value: z.string(), // value can be uuid of exam courses or 'all'
        label: z.string(),
      }),
    )
    .optional(),
  location: z.object({
    province: z
      .object({
        label: z.string(),
        value: z.string().uuid(),
      })
      .nullish(),
    locationName: z
      .object({
        label: z.string(),
        value: z.string().uuid(),
      })
      .nullish(),
    room: z.record(z.string().uuid(), z.boolean().nullish()).optional(),
    seat: z.union([z.number(), z.literal('')]).optional(),
  }),
  mainProctors: z
    .array(
      z.object({
        value: z.string().uuid(),
        label: z.string(),
      }),
    )
    .optional(),
  examDate: z.object({
    date: z.date().nullish(),
    startTime: z.date().nullish(),
    endTime: z.date().nullish(),
  }),
  sessions: z.array(
    z.object({
      startedAt: z.date().nullish(),
      endedAt: z.date().nullish(),
    }),
  ),
  eventStartDate: z.date().nullish(),
  eventEndDate: z.date().nullish(),
  groupType: z
    .object({
      label: z.string(),
      value: z.nativeEnum(ApplicantGroupType),
    })
    .nullish(),
  groupCode: z.string().nullish(),
});

export type ExamEventCreationFormType = z.infer<typeof BaseExamEventCreationFormSchema>;

let superRefineCtx: z.RefinementCtx | null = null;

type SessionKeys = 'startedAt' | 'endedAt';

type BaseExamEventCreationFormTypeKeys =
  Path<ExamEventCreationFormType> | number | SessionKeys | 'root';

function addValidationIssue(args: { path: BaseExamEventCreationFormTypeKeys[]; message: string }) {
  if (!superRefineCtx) {
    console.error('BaseExamEventCreationFormSchema: superRefineCtx is not defined');
    return;
  }

  const { path, message } = args;
  superRefineCtx.addIssue({
    code: ZodIssueCode.custom,
    path,
    message,
  });
}
const allowCharactersRegex = /^[ก-๏a-zA-Z0-9\s]*$/;

function validateAllowCharacters(input: string, path: BaseExamEventCreationFormTypeKeys[]) {
  if (!allowCharactersRegex.test(input)) {
    addValidationIssue({
      path,
      message: 'อนุญาตเฉพาะ ก-ฮ, a-z, A-Z, 0-9 เท่านั้น',
    });
  }
}

const EXAM_EVENT_NAME_MIN_LENGTH = 1;
const EXAM_EVENT_NAME_MAX_LENGTH = 1024;

function validateExamEventName(data: ExamEventCreationFormType) {
  const { examEventName } = data;

  if (!examEventName) {
    addValidationIssue({
      path: ['examEventName'],
      message: 'กรุณากรอกชื่อรอบสอบ',
    });
    return;
  }

  const examEventNameLength = examEventName.trim().length;

  if (examEventNameLength < EXAM_EVENT_NAME_MIN_LENGTH) {
    addValidationIssue({
      path: ['examEventName'],
      message: 'กรุณากรอกชื่อรอบสอบ',
    });
  } else if (examEventNameLength > EXAM_EVENT_NAME_MAX_LENGTH) {
    addValidationIssue({
      path: ['examEventName'],
      message: `ความยาวต้องไม่เกิน ${EXAM_EVENT_NAME_MAX_LENGTH} ตัวอักษร`,
    });
  }

  validateAllowCharacters(examEventName, ['examEventName']);
}

function validateExamType(data: ExamEventCreationFormType) {
  const { examType } = data;

  if (!examType) {
    addValidationIssue({
      path: ['examType'],
      message: 'กรุณาเลือก Exam Type',
    });
  }
}

function validateRegistrationType(data: ExamEventCreationFormType) {
  const { registrationType } = data;

  if (!registrationType) {
    addValidationIssue({
      path: ['registrationType'],
      message: 'กรุณาเลือก Registration Type',
    });
  }
}

function validateExamineeImportMethod(data: ExamEventCreationFormType) {
  const { examineeImportMethod } = data;

  if (!examineeImportMethod) {
    addValidationIssue({
      path: ['examineeImportMethod'],
      message: 'กรุณาเลือกวิธีนำเข้าผู้สอบ',
    });
  }
}

const EXAM_EVENT_SEAT_MIN_LENGTH = 1;

function validateLocation(data: ExamEventCreationFormType) {
  const { location } = data;

  if (!location.province?.value) {
    addValidationIssue({
      path: ['location.province'],
      message: 'กรุณาเลือกจังหวัด',
    });
  }

  if (!location.locationName?.value) {
    addValidationIssue({
      path: ['location.locationName'],
      message: 'กรุณาเลือกสถานที่สอบ/หน่วยงาน',
    });
  }

  if (!location.room) {
    addValidationIssue({
      path: ['location.room'],
      message: 'กรุณาเลือกห้องสอบ',
    });
  }

  if (!location.seat || (typeof location.seat === 'string' && location.seat === '')) {
    addValidationIssue({
      path: ['location.seat'],
      message: 'กรุณากรอกจำนวนที่นั่ง',
    });
  }

  if (location.room) {
    const selectedRooms = Object.values(location.room).filter((room) => room);
    if (selectedRooms.length === 0) {
      addValidationIssue({
        path: ['location.room'],
        message: 'กรุณาเลือกห้องสอบ',
      });
    }
  }

  if (location.seat && location.seat < EXAM_EVENT_SEAT_MIN_LENGTH) {
    addValidationIssue({
      path: ['location.seat'],
      message: `จำนวนที่นั่งต้องมากกว่า ${EXAM_EVENT_SEAT_MIN_LENGTH - 1}`,
    });
  }
}

const MINIMUM_EXAM_DURATION_MINUTES = 15;

function validateExamDate(data: ExamEventCreationFormType) {
  const { examDate } = data;

  if (!examDate.date) {
    addValidationIssue({
      path: ['examDate.date'],
      message: 'ระบุวันที่สอบ',
    });
  }

  if (!examDate.startTime) {
    addValidationIssue({
      path: ['examDate.startTime'],
      message: 'ระบุเวลาเริ่มต้น',
    });
  }

  if (!examDate.endTime) {
    addValidationIssue({
      path: ['examDate.endTime'],
      message: 'ระบุเวลาสิ้นสุด',
    });
  }

  if (examDate.date && examDate.startTime && examDate.endTime) {
    const examDateWithStartTime = dayjs(examDate.date)
      .set('hour', examDate.startTime.getHours())
      .set('minute', examDate.startTime.getMinutes());
    const examDateWithEndTime = dayjs(examDate.date)
      .set('hour', examDate.endTime.getHours())
      .set('minute', examDate.endTime.getMinutes());

    if (examDateWithEndTime.isBefore(examDateWithStartTime)) {
      addValidationIssue({
        path: ['examDate.endTime'],
        message: 'เวลาสิ้นสุดต้องมากกว่าเวลาเริ่มต้น',
      });
    }

    const diffTimeInMinutes = examDateWithEndTime.diff(examDateWithStartTime, 'minute');
    if (diffTimeInMinutes < MINIMUM_EXAM_DURATION_MINUTES) {
      addValidationIssue({
        path: ['examDate.endTime'],
        message: `เวลาสอบต้องมีความยาวอย่างน้อย ${MINIMUM_EXAM_DURATION_MINUTES} นาที`,
      });
    }
  }
}

function validateExamCourses(data: ExamEventCreationFormType) {
  const { examCourses } = data;

  if (!examCourses || examCourses.length === 0) {
    addValidationIssue({
      path: ['examCourses'],
      message: 'กรุณาเลือกหลักสูตรที่เปิดสอบ',
    });

    return;
  }

  if (data.examType === ExamType.Paper) {
    if (examCourses.length > 1) {
      addValidationIssue({
        path: ['examCourses'],
        message: 'สามารถเลือกได้เพียง 1 หลักสูตรเท่านั้น',
      });
    }

    if (examCourses.some((course) => course.value === 'all')) {
      addValidationIssue({
        path: ['examCourses'],
        message: 'ไม่อนุญาตให้เลือกทุกหลักสูตร',
      });
    }
  }
}

const MINIMUM_SESSIONS = 2;

const MINIMUM_SESSION_DURATION_MINUTES = 15;
const MAXIMUM_SESSION_DURATION_MINUTES = 12 * 60; // 12 hours

const MAXIMUM_TOTAL_DURATION_MINUTES = 24 * 60; // 24 hours

const MINIMUM_SESSION_GAP_MINUTES = 30;

function validateSessions(data: ExamEventCreationFormType) {
  const { examDate, examType, sessions } = data;

  if (examType === ExamType.Paper && sessions.length > 1) {
    addValidationIssue({
      path: ['sessions'],
      message: 'สามารถเลือกได้เพียง 1 ช่วงสอบเท่านั้น',
    });
  }

  if (sessions.length > 0) {
    let totalDuration = 0;
    const sessionsPath: BaseExamEventCreationFormTypeKeys[] = ['sessions', 'root'];

    if (sessions.length < MINIMUM_SESSIONS) {
      addValidationIssue({
        path: sessionsPath,
        message: `ช่วงสอบต้องมีอย่างน้อย ${MINIMUM_SESSIONS} ช่วง`,
      });
    }

    for (const [index, session] of sessions.entries()) {
      const startedAtPath: BaseExamEventCreationFormTypeKeys[] = ['sessions', index, 'startedAt'];
      const endedAtPath: BaseExamEventCreationFormTypeKeys[] = ['sessions', index, 'endedAt'];

      if (!session.startedAt) {
        addValidationIssue({
          path: startedAtPath,
          message: 'ระบุเวลาเริ่มต้น',
        });
      }

      if (!session.endedAt) {
        addValidationIssue({
          path: endedAtPath,
          message: 'ระบุเวลาสิ้นสุด',
        });
      }

      const startedAt = dayjs(session.startedAt).startOf('minute');
      const endedAt = dayjs(session.endedAt).startOf('minute');

      if (!endedAt.isAfter(startedAt)) {
        addValidationIssue({
          path: endedAtPath,
          message: 'เวลาสิ้นสุดต้องมากกว่าเวลาเริ่มต้น',
        });
      }

      if (examDate.startTime && examDate.endTime) {
        if (!startedAt.isBetween(examDate.startTime, examDate.endTime, 'minutes', '[]')) {
          addValidationIssue({
            path: startedAtPath,
            message: 'เวลาเริ่มต้นอยู่นอกช่วงเวลาสอบทั้งหมด',
          });
        }

        if (!endedAt.isBetween(examDate.startTime, examDate.endTime, 'minutes', '[]')) {
          addValidationIssue({
            path: endedAtPath,
            message: 'เวลาสิ้นสุดอยู่นอกช่วงเวลาสอบทั้งหมด',
          });
        }
      }

      const previousSession = sessions[index - 1];
      if (previousSession) {
        const previousEndedAt = dayjs(previousSession.endedAt).startOf('minute');
        if (startedAt.isBefore(previousEndedAt)) {
          addValidationIssue({
            path: startedAtPath,
            message: 'เวลาเริ่มต้นต้องมากกว่าหรือเท่ากับเวลาสิ้นสุดช่วงก่อนหน้า',
          });
        }

        if (startedAt.diff(previousEndedAt, 'minute') < MINIMUM_SESSION_GAP_MINUTES) {
          addValidationIssue({
            path: startedAtPath,
            message: `เวลาเริ่มต้นต้องห่างจากเวลาสิ้นสุดช่วงก่อนหน้าอย่างน้อย ${MINIMUM_SESSION_GAP_MINUTES} นาที`,
          });
        }
      }

      if (!session.startedAt || !session.endedAt) {
        continue; // Skip validation if either time is not provided
      }

      const diffInMinutes = endedAt.diff(startedAt, 'minute');
      totalDuration += diffInMinutes;
      const sessionPath: BaseExamEventCreationFormTypeKeys[] = ['sessions', index, 'root'];
      if (diffInMinutes < MINIMUM_SESSION_DURATION_MINUTES) {
        addValidationIssue({
          path: sessionPath,
          message: `ระยะเวลาสอบต้องมีความยาวอย่างน้อย ${MINIMUM_SESSION_DURATION_MINUTES} นาที`,
        });
      }

      if (diffInMinutes > MAXIMUM_SESSION_DURATION_MINUTES) {
        addValidationIssue({
          path: sessionPath,
          message: `ระยะเวลาสอบต้องมีความยาวไม่เกิน ${MAXIMUM_SESSION_DURATION_MINUTES / 60} ชั่วโมง`,
        });
      }
    }

    if (totalDuration > MAXIMUM_TOTAL_DURATION_MINUTES) {
      addValidationIssue({
        path: sessionsPath,
        message: `ระยะเวลาสอบรวมทั้งหมดต้องมีความยาวไม่เกิน ${MAXIMUM_TOTAL_DURATION_MINUTES / 60} ชั่วโมง`,
      });
    }
  }
}

const MINIMUM_CLOSE_REGISTRATION_DURATION_BEFORE_NOW = 15;
const MINIMUM_CLOSE_REGISTRATION_DURATION_BEFORE_EXAM = 30;

function validateEventDates(data: ExamEventCreationFormType) {
  const { eventStartDate, eventEndDate, examDate } = data;

  if (!eventStartDate) {
    addValidationIssue({
      path: ['eventStartDate'],
      message: 'ระบุวันที่เปิดรับสมัคร',
    });
  }

  if (!eventEndDate) {
    addValidationIssue({
      path: ['eventEndDate'],
      message: 'ระบุวันที่ปิดรับสมัคร',
    });
  }

  if (eventStartDate && eventEndDate) {
    const eventStartDateAsDayjs = dayjs(eventStartDate);
    const eventEndDateAsDayjs = dayjs(eventEndDate);

    if (eventEndDateAsDayjs.isBefore(eventStartDateAsDayjs)) {
      addValidationIssue({
        path: ['eventEndDate'],
        message: 'เวลาปิดรับสมัครต้องมากกว่าเวลาที่เปิดรับสมัคร',
      });
    }

    const currentTime = dayjs().startOf('minute');
    const diffInMinutes = eventEndDateAsDayjs.diff(currentTime, 'minute');
    if (diffInMinutes < MINIMUM_CLOSE_REGISTRATION_DURATION_BEFORE_NOW) {
      addValidationIssue({
        path: ['eventEndDate'],
        message: `เวลาปิดรับสมัครต้องมากกว่าเวลาปัจจุบันอย่างน้อย ${MINIMUM_CLOSE_REGISTRATION_DURATION_BEFORE_NOW} นาที`,
      });
    }

    if (examDate.date && examDate.startTime) {
      const examDateWithStartTime = dayjs(examDate.date)
        .set('hour', examDate.startTime.getHours())
        .set('minute', examDate.startTime.getMinutes());

      const diffInMinutesToExam = examDateWithStartTime.diff(eventEndDate, 'minute');
      if (diffInMinutesToExam < MINIMUM_CLOSE_REGISTRATION_DURATION_BEFORE_EXAM) {
        addValidationIssue({
          path: ['eventEndDate'],
          message: `เวลาปิดรับสมัครต้องไม่เกิน ${MINIMUM_CLOSE_REGISTRATION_DURATION_BEFORE_EXAM} นาทีก่อนเริ่มสอบ`,
        });
      }
    }
  }
}

const APPLICATION_GROUP_CODE_MAX_LENGTH = 256;

function validateGroupCode(data: ExamEventCreationFormType) {
  const { groupCode } = data;

  if (groupCode) {
    if (groupCode.length > APPLICATION_GROUP_CODE_MAX_LENGTH) {
      addValidationIssue({
        path: ['groupCode'],
        message: `ความยาวต้องไม่เกิน ${APPLICATION_GROUP_CODE_MAX_LENGTH} ตัวอักษร`,
      });
    }

    validateAllowCharacters(groupCode, ['groupCode']);
  }
}

export const ExamEventCreationFormSchema = BaseExamEventCreationFormSchema.superRefine(
  (data, ctx) => {
    superRefineCtx = ctx;
    validateExamEventName(data);
    validateExamType(data);
    validateRegistrationType(data);
    validateExamineeImportMethod(data);
    validateLocation(data);
    validateExamDate(data);
    validateExamCourses(data);
    validateSessions(data);
    validateEventDates(data);
    validateGroupCode(data);
  },
);
